import * as React from "react";
import { useTranslation, Trans } from "react-i18next";
import type { LocalisedProductConfig } from "@shared/types";
import { Badge } from "@bcc/components/ui/badge";
import type { BagSizeGroup } from "../types";
import { REQUIRED_GRIND } from "../constants";
import { setRequiredVariantHintTestId } from "./required-variant-hint.utils";

export interface RequiredVariantHintProps {
  bagSizeGroupsWithRequiredGrindType: BagSizeGroup[];
  requiredGrind: string;
  grindConfig?: LocalisedProductConfig["grind"];
}

export const RequiredVariantHint = ({
  bagSizeGroupsWithRequiredGrindType,
  requiredGrind = REQUIRED_GRIND,
  grindConfig,
}: RequiredVariantHintProps) => {
  const { t } = useTranslation();

  // Get the translated label for the required grind
  const requiredGrindLabel = grindConfig?.find((option) => option.value === requiredGrind)?.label ?? requiredGrind;

  // Helper to get localized label for any grind type
  const getGrindLabel = (grindType: string) => {
    return grindConfig?.find((option) => option.value === grindType)?.label ?? grindType;
  };

  // Don't render if no bag size groups
  if (!bagSizeGroupsWithRequiredGrindType || bagSizeGroupsWithRequiredGrindType.length === 0) {
    return null;
  }

  return (
    <div className="rounded-lg border p-3 bg-muted/50" data-testid={setRequiredVariantHintTestId("root")}>
      <p className="text-sm font-medium mb-2">
        <Trans
          i18nKey="components.VariantForm.variantFormRequiredGrindTypesMessage"
          values={{ requiredGrindTranslation: requiredGrindLabel }}
        >
          {"Required grind type for each bag size: "}
          <strong className="font-bold">{requiredGrindLabel}</strong>
          {"."}
        </Trans>
      </p>

      {bagSizeGroupsWithRequiredGrindType.length > 0 && (
        <>
          <p className="text-sm mb-1">{t("components.VariantForm.selectedBagSizes")}</p>
          <ul className="space-y-1">
            {bagSizeGroupsWithRequiredGrindType.map((bagSizeGroup) => {
              const hasRequiredGrind = bagSizeGroup.grindTypes.includes(requiredGrind);

              return (
                <li key={bagSizeGroup.bagSize} className="text-sm">
                  <span className="font-medium">{bagSizeGroup.bagSize}:</span>{" "}
                  <div className="inline-flex flex-wrap gap-1 ml-1">
                    {bagSizeGroup.grindTypes.map((grindType) => {
                      const grindLabel = getGrindLabel(grindType);

                      return (
                        <Badge
                          key={grindType}
                          variant={hasRequiredGrind ? "default" : "secondary"}
                          className={hasRequiredGrind ? "bg-green-500 hover:bg-green-600 text-white border-transparent" : ""}
                          data-testid={setRequiredVariantHintTestId(`grind-badge-${grindType.replace(/\s+/g, "")}`)}
                        >
                          {grindLabel}
                        </Badge>
                      );
                    })}
                  </div>
                </li>
              );
            })}
          </ul>
        </>
      )}
    </div>
  );
};
